/**
 * Noise sample generation for SFX grains (white) and ambience beds (pink).
 * Seeded with the core Rng so buffers are reproducible; the loop point is
 * crossfaded so a looping bed never clicks.
 *
 * Pure and DOM-free (unit-tested in node); graph code copies the samples into
 * an AudioBuffer.
 */
import { Rng } from '@dropstack/core';

export type NoiseColour = 'white' | 'pink';

/** Samples used to crossfade the end of a looping buffer into its start. */
export const LOOP_CROSSFADE_SAMPLES = 2048;

/** Peak level the generated noise is normalised to. */
export const NOISE_PEAK = 0.9;

function generate(count: number, colour: NoiseColour, rng: Rng): Float32Array {
  const out = new Float32Array(count);
  if (colour === 'white') {
    for (let i = 0; i < count; i++) out[i] = rng.next() * 2 - 1;
    return out;
  }
  // Paul Kellet's refined pink-noise filter (−3 dB/octave).
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
  for (let i = 0; i < count; i++) {
    const w = rng.next() * 2 - 1;
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    out[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
    b6 = w * 0.115926;
  }
  return out;
}

/**
 * `length` samples of loopable noise. The first LOOP_CROSSFADE_SAMPLES are
 * blended with the samples that would have followed the end, so playback
 * wraps from the last sample to the first without a discontinuity.
 */
export function makeLoopableNoise(length: number, colour: NoiseColour, seed: number): Float32Array {
  const fade = Math.min(LOOP_CROSSFADE_SAMPLES, Math.floor(length / 4));
  const raw = generate(length + fade, colour, new Rng(seed));
  const out = raw.slice(0, length);
  for (let i = 0; i < fade; i++) {
    const w = i / fade;
    out[i] = raw[i]! * w + raw[length + i]! * (1 - w);
  }
  let peak = 0;
  for (let i = 0; i < length; i++) peak = Math.max(peak, Math.abs(out[i]!));
  if (peak > 0) {
    const k = NOISE_PEAK / peak;
    for (let i = 0; i < length; i++) out[i] = out[i]! * k;
  }
  return out;
}
